/**
 * Cash Flow Statement preset view — embedded in PivotWorkbench.
 *
 * Hardcoded structured layout that mirrors the "Апрель 2026" /
 * "Май 2026" sheets from Data/Cashflow_Apr-May 2026.xlsx. Treasury
 * reviews the same entity rows every month with columns for Opening
 * Balance, Regulatory Minimum, Budgeted Plan/Actual (Revenue +
 * Expense), Unplanned Plan/Actual (Revenue + Expense), and Ending
 * Balance. Every money block further splits into Fiat / Crypto /
 * Total sub-columns.
 *
 * Data sources:
 *  - /api/fin/analytics-data → cashflow[] (amount, actual_amount,
 *    payment_method, bucket, status, type, category, month, year)
 *  - /api/fin/analytics-data → balances[] (fiat_balance, crypto_balance)
 *  - /api/fin/entities (for name / min_capital_eur lookup)
 *
 * Row order comes from statement-layout.ts (Excel-exact override)
 * rather than fin.entities.parent_entity.
 */

import { useMemo, useState, Fragment } from 'react';
import { useFetch } from '@/lib/fin/use-fetch';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, ChevronRight, AlertCircle, Calendar } from 'lucide-react';
import { STATEMENT_LAYOUT, type StatementRowSpec } from './statement-layout';

/* ───────── types ───────── */

interface CashFlowRow {
  month: string;     // "Jan".."Dec"
  year: string;
  entity: string;    // display name from fin.entities.name
  group: string;
  type: 'revenue' | 'expense' | string;
  category: string;
  source: string;
  status: string;                     // 'planned' | 'completed' | ...
  payment_method: string;             // 'fiat' | 'crypto'
  bucket: 'budgeted' | 'unplanned' | string;
  amount: number;                     // planned amount in original currency
  actual_amount: number | null;       // realised amount (null when not yet paid)
  actual_month: string | null;
  actual_year: string | null;
}

interface BalanceRow {
  month: string;
  year: string;
  entity: string;
  group: string;
  fiat_balance: number;
  crypto_balance: number;
  total_balance: number;
  fiat_currency: string;
  crypto_currency: string;
  notes: string;
}

interface AnalyticsData {
  cashflow: CashFlowRow[];
  balances: BalanceRow[];
  pl: unknown[];
  budget: unknown[];
}

interface EntityRecord {
  id: string;
  name: string;
  legal_name: string | null;
  parent_entity: string | null;
  parent_name: string | null;
  group_tag: string | null;
  is_active: boolean;
  min_capital_eur: number | string | null;
}

/* ───────── formatting helpers ───────── */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtMoney(v: number | null | undefined, opts: { dim?: boolean } = {}): string {
  if (v == null || !isFinite(v) || v === 0) return opts.dim ? '—' : '—';
  const a = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (a >= 1e6) return `${sign}${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}${(a / 1e3).toFixed(0)}k`;
  return `${sign}${a.toFixed(0)}`;
}

/* ───────── money-block aggregate ─────────

   Every "money block" (Plan Revenue Budgeted, Actual Expenses
   Unplanned, etc.) tracks three numbers: fiat, crypto, total.
   `total` is simply `fiat + crypto` — we don't do FX conversion; the
   values are already denominated and summed in their native reporting
   currency. Amounts are treated as EUR-equivalent for display.       */

interface MoneyBlock {
  fiat: number;
  crypto: number;
  total: number;
}

function emptyBlock(): MoneyBlock {
  return { fiat: 0, crypto: 0, total: 0 };
}

function addMoney(b: MoneyBlock, pm: string, amount: number) {
  if (pm === 'crypto') b.crypto += amount;
  else b.fiat += amount;
  b.total += amount;
}

function sumBlocks(blocks: MoneyBlock[]): MoneyBlock {
  return blocks.reduce((acc, b) => {
    acc.fiat += b.fiat;
    acc.crypto += b.crypto;
    acc.total += b.total;
    return acc;
  }, emptyBlock());
}

/* ───────── entity totals for one month ───────── */

interface EntityTotals {
  // Opening + regulatory
  openingBalance: MoneyBlock;
  minCapital: number;   // single EUR value from fin.entities

  // Budgeted block (ПЛАН. / ФАКТ.)
  budgetedPlanRev: MoneyBlock;
  budgetedActualRev: MoneyBlock;
  budgetedPlanExp: MoneyBlock;
  budgetedActualExp: MoneyBlock;

  // Unplanned block (ВНЕПЛ. ПЛАН / ВНЕПЛ. ФАКТ)
  unplannedPlanRev: MoneyBlock;
  unplannedActualRev: MoneyBlock;
  unplannedPlanExp: MoneyBlock;
  unplannedActualExp: MoneyBlock;

  // Ending balance — computed as Opening + all Actuals (net)
  endingBalance: MoneyBlock;

  // Per-category drill-down
  revenueByCategory: Map<string, { plan: number; actual: number; bucket: string }>;
  expenseByCategory: Map<string, { plan: number; actual: number; bucket: string }>;
}

function emptyTotals(): EntityTotals {
  return {
    openingBalance: emptyBlock(),
    minCapital: 0,
    budgetedPlanRev: emptyBlock(),
    budgetedActualRev: emptyBlock(),
    budgetedPlanExp: emptyBlock(),
    budgetedActualExp: emptyBlock(),
    unplannedPlanRev: emptyBlock(),
    unplannedActualRev: emptyBlock(),
    unplannedPlanExp: emptyBlock(),
    unplannedActualExp: emptyBlock(),
    endingBalance: emptyBlock(),
    revenueByCategory: new Map(),
    expenseByCategory: new Map(),
  };
}

function aggregate(
  cashflow: CashFlowRow[],
  balances: BalanceRow[],
  entities: EntityRecord[],
  month: string, year: string,
): Map<string, EntityTotals> {
  const out = new Map<string, EntityTotals>();
  const byName = new Map<string, EntityRecord>();
  for (const e of entities) {
    byName.set(e.name.toLowerCase(), e);
    const t = emptyTotals();
    t.minCapital = Number(e.min_capital_eur) || 0;
    out.set(e.id, t);
  }

  // Cash flow rows. A row is classified into one of 4 buckets:
  // (budget × status) = budgetedPlan, budgetedActual, unplannedPlan, unplannedActual.
  //
  // The PLAN amount always uses `amount` (the originally-noted figure,
  // dated to `expected_date`). The ACTUAL amount uses `actual_amount`
  // dated to `actual_date` (which may be null when nothing happened
  // yet — the row is pure plan in that case).
  for (const r of cashflow) {
    const ent = byName.get(String(r.entity).toLowerCase());
    if (!ent) continue;
    const t = out.get(ent.id)!;
    const isCrypto = r.payment_method === 'crypto';
    const bucket = r.bucket === 'unplanned' ? 'unplanned' : 'budgeted';

    // Plan side (expected)
    if (r.month === month && r.year === year && r.amount) {
      const target =
        r.type === 'revenue'
          ? (bucket === 'budgeted' ? t.budgetedPlanRev : t.unplannedPlanRev)
          : (bucket === 'budgeted' ? t.budgetedPlanExp : t.unplannedPlanExp);
      addMoney(target, r.payment_method, r.amount);

      // Category drill-down
      const catMap = r.type === 'revenue' ? t.revenueByCategory : t.expenseByCategory;
      const key = r.category || 'Uncategorized';
      const entry = catMap.get(key) || { plan: 0, actual: 0, bucket };
      entry.plan += r.amount;
      entry.bucket = bucket;
      catMap.set(key, entry);
    }

    // Actual side (realised)
    if (
      r.actual_amount != null &&
      r.actual_month === month && r.actual_year === year
    ) {
      const target =
        r.type === 'revenue'
          ? (bucket === 'budgeted' ? t.budgetedActualRev : t.unplannedActualRev)
          : (bucket === 'budgeted' ? t.budgetedActualExp : t.unplannedActualExp);
      addMoney(target, r.payment_method, r.actual_amount);

      const catMap = r.type === 'revenue' ? t.revenueByCategory : t.expenseByCategory;
      const key = r.category || 'Uncategorized';
      const entry = catMap.get(key) || { plan: 0, actual: 0, bucket };
      entry.actual += r.actual_amount;
      catMap.set(key, entry);
    }

    // Suppress unused vars
    void isCrypto;
  }

  // Opening balance for this month
  for (const b of balances) {
    if (b.month !== month || b.year !== year) continue;
    const ent = byName.get(String(b.entity).toLowerCase());
    if (!ent) continue;
    const t = out.get(ent.id)!;
    t.openingBalance = {
      fiat: Number(b.fiat_balance) || 0,
      crypto: Number(b.crypto_balance) || 0,
      total: (Number(b.fiat_balance) || 0) + (Number(b.crypto_balance) || 0),
    };
  }

  // Ending balance = Opening + net actual flow (income - expenses).
  // Includes both budgeted and unplanned actuals since those are the
  // only cash that actually moved.
  for (const t of out.values()) {
    const totalActualRev = sumBlocks([t.budgetedActualRev, t.unplannedActualRev]);
    const totalActualExp = sumBlocks([t.budgetedActualExp, t.unplannedActualExp]);
    t.endingBalance = {
      fiat: t.openingBalance.fiat + totalActualRev.fiat - totalActualExp.fiat,
      crypto: t.openingBalance.crypto + totalActualRev.crypto - totalActualExp.crypto,
      total: t.openingBalance.total + totalActualRev.total - totalActualExp.total,
    };
  }

  return out;
}

/* ───────── aggregate helpers for subtotal rows ───────── */

function sumEntityTotals(totals: Map<string, EntityTotals>, entityIds: string[]): EntityTotals {
  const pick = (getter: (t: EntityTotals) => MoneyBlock): MoneyBlock =>
    entityIds.reduce((acc, id) => {
      const t = totals.get(id);
      if (!t) return acc;
      const b = getter(t);
      return { fiat: acc.fiat + b.fiat, crypto: acc.crypto + b.crypto, total: acc.total + b.total };
    }, emptyBlock());

  const minCapital = entityIds.reduce((s, id) => s + (totals.get(id)?.minCapital || 0), 0);

  return {
    openingBalance: pick(t => t.openingBalance),
    minCapital,
    budgetedPlanRev: pick(t => t.budgetedPlanRev),
    budgetedActualRev: pick(t => t.budgetedActualRev),
    budgetedPlanExp: pick(t => t.budgetedPlanExp),
    budgetedActualExp: pick(t => t.budgetedActualExp),
    unplannedPlanRev: pick(t => t.unplannedPlanRev),
    unplannedActualRev: pick(t => t.unplannedActualRev),
    unplannedPlanExp: pick(t => t.unplannedPlanExp),
    unplannedActualExp: pick(t => t.unplannedActualExp),
    endingBalance: pick(t => t.endingBalance),
    revenueByCategory: new Map(),
    expenseByCategory: new Map(),
  };
}

/* ───────── render helpers ───────── */

/**
 * A 3-sub-column money cell (Fiat | Crypto | Total) rendered as three
 * adjacent TableCells. The caller controls the bg colour class via
 * `tone`.
 */
function MoneyBlockCells({ block, tone, strong }: { block: MoneyBlock; tone: string; strong?: boolean }) {
  const cls = `text-right font-mono text-[10px] ${tone} ${strong ? 'font-semibold' : ''}`;
  return (
    <>
      <TableCell className={cls}>{fmtMoney(block.fiat)}</TableCell>
      <TableCell className={cls}>{fmtMoney(block.crypto)}</TableCell>
      <TableCell className={`${cls} border-r`}>{fmtMoney(block.total)}</TableCell>
    </>
  );
}

/* ───────── component ───────── */

interface StatementViewProps {
  defaultYear?: string;
  defaultMonth?: string;
}

export default function StatementView({
  defaultYear = '2026',
  defaultMonth = 'Apr',
}: StatementViewProps) {
  const { data: analytics, loading: loadingCf } =
    useFetch<AnalyticsData>('/api/fin/analytics-data');
  const { data: entities, loading: loadingEntities } =
    useFetch<EntityRecord[]>('/api/fin/entities');

  const [month, setMonth] = useState(defaultMonth);
  const [year, setYear] = useState(defaultYear);
  const [expandedEntities, setExpandedEntities] = useState<Set<string>>(new Set());

  const totals = useMemo(() => {
    if (!analytics || !entities) return new Map<string, EntityTotals>();
    return aggregate(
      analytics.cashflow || [],
      analytics.balances || [],
      entities,
      month,
      year,
    );
  }, [analytics, entities, month, year]);

  const availableYears = useMemo(() => {
    if (!analytics) return [year];
    const s = new Set<string>();
    for (const r of analytics.cashflow) s.add(r.year);
    for (const r of analytics.balances) s.add(r.year);
    const arr = [...s].sort();
    return arr.length > 0 ? arr : [year];
  }, [analytics, year]);

  const loading = loadingCf || loadingEntities;
  if (loading && !analytics) {
    return <div className="p-6"><Skeleton className="h-96 w-full" /></div>;
  }

  const renderRow = (row: StatementRowSpec, idx: number) => {
    if (row.kind === 'group') {
      return (
        <TableRow key={`g-${idx}`} className="bg-rose-100/60 dark:bg-rose-950/30 hover:bg-rose-100/70 dark:hover:bg-rose-950/40">
          <TableCell colSpan={33} className="py-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-rose-900 dark:text-rose-200">
              {row.label}
            </span>
          </TableCell>
        </TableRow>
      );
    }

    if (row.kind === 'entity') {
      const t = totals.get(row.entityId) || emptyTotals();
      const isExpanded = expandedEntities.has(row.entityId);
      const hasData =
        t.budgetedPlanRev.total + t.budgetedActualRev.total +
        t.budgetedPlanExp.total + t.budgetedActualExp.total +
        t.unplannedPlanRev.total + t.unplannedActualRev.total +
        t.unplannedPlanExp.total + t.unplannedActualExp.total !== 0;
      const depth = row.depth || 0;
      const revCats = [...t.revenueByCategory.entries()]
        .sort((a, b) => (b[1].actual + b[1].plan) - (a[1].actual + a[1].plan));
      const expCats = [...t.expenseByCategory.entries()]
        .sort((a, b) => (b[1].actual + b[1].plan) - (a[1].actual + a[1].plan));

      return (
        <Fragment key={`e-${idx}-${row.entityId}`}>
          <TableRow
            className={`hover:bg-muted/30 ${hasData ? 'cursor-pointer' : 'opacity-60'}`}
            onClick={() => hasData && setExpandedEntities(s => {
              const next = new Set(s);
              if (next.has(row.entityId)) next.delete(row.entityId); else next.add(row.entityId);
              return next;
            })}
          >
            {/* Entity label with indent + chevron */}
            <TableCell className="py-1.5 text-xs sticky left-0 bg-background z-10 border-r">
              <div className="flex items-center gap-1" style={{ paddingLeft: `${depth * 14}px` }}>
                {depth > 0 && <span className="text-muted-foreground">└</span>}
                {hasData ? (isExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />) : <span className="w-3" />}
                <span className="font-medium truncate">{row.label || row.entityId}</span>
              </div>
            </TableCell>

            {/* Opening Balance */}
            <MoneyBlockCells block={t.openingBalance} tone="" />

            {/* Reg Min — single value */}
            <TableCell className="text-right font-mono text-[10px] text-muted-foreground border-r">
              {fmtMoney(t.minCapital)}
            </TableCell>

            {/* BUDGETED — Plan Rev */}
            <MoneyBlockCells block={t.budgetedPlanRev} tone="bg-emerald-50/40 dark:bg-emerald-950/15" />
            {/* BUDGETED — Actual Rev */}
            <MoneyBlockCells block={t.budgetedActualRev} tone="bg-emerald-50/60 dark:bg-emerald-950/25" strong />
            {/* BUDGETED — Plan Exp */}
            <MoneyBlockCells block={t.budgetedPlanExp} tone="bg-rose-50/40 dark:bg-rose-950/15" />
            {/* BUDGETED — Actual Exp */}
            <MoneyBlockCells block={t.budgetedActualExp} tone="bg-rose-50/60 dark:bg-rose-950/25" strong />

            {/* UNPLANNED — Plan Rev */}
            <MoneyBlockCells block={t.unplannedPlanRev} tone="bg-emerald-100/40 dark:bg-emerald-900/20" />
            {/* UNPLANNED — Actual Rev */}
            <MoneyBlockCells block={t.unplannedActualRev} tone="bg-emerald-100/60 dark:bg-emerald-900/30" strong />
            {/* UNPLANNED — Plan Exp */}
            <MoneyBlockCells block={t.unplannedPlanExp} tone="bg-rose-100/40 dark:bg-rose-900/20" />
            {/* UNPLANNED — Actual Exp */}
            <MoneyBlockCells block={t.unplannedActualExp} tone="bg-rose-100/60 dark:bg-rose-900/30" strong />

            {/* Ending Balance */}
            <MoneyBlockCells block={t.endingBalance} tone="bg-muted/30" strong />
          </TableRow>

          {/* Category drill-down */}
          {isExpanded && revCats.length > 0 && (
            <TableRow className="bg-emerald-50/20 dark:bg-emerald-950/10">
              <TableCell colSpan={33} className="py-0.5 sticky left-0 bg-background z-10 border-r">
                <div className="text-[9px] font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider"
                  style={{ paddingLeft: `${(depth + 2) * 14}px` }}>
                  Revenue by category
                </div>
              </TableCell>
            </TableRow>
          )}
          {isExpanded && revCats.map(([cat, v]) => (
            <TableRow key={`rev-${row.entityId}-${cat}`} className="bg-emerald-50/10 dark:bg-emerald-950/5 text-muted-foreground">
              <TableCell colSpan={33} className="py-0.5 sticky left-0 bg-background z-10 border-r">
                <div className="flex items-center justify-between gap-4 text-[10px]"
                  style={{ paddingLeft: `${(depth + 3) * 14}px` }}>
                  <span className="truncate">{cat.replace(/_/g, ' ')}</span>
                  <Badge variant="outline" className="text-[8px] shrink-0">{v.bucket}</Badge>
                  <span className="font-mono shrink-0">plan {fmtMoney(v.plan)}</span>
                  <span className="font-mono shrink-0">actual {fmtMoney(v.actual)}</span>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {isExpanded && expCats.length > 0 && (
            <TableRow className="bg-rose-50/20 dark:bg-rose-950/10">
              <TableCell colSpan={33} className="py-0.5 sticky left-0 bg-background z-10 border-r">
                <div className="text-[9px] font-semibold text-rose-700 dark:text-rose-400 uppercase tracking-wider"
                  style={{ paddingLeft: `${(depth + 2) * 14}px` }}>
                  Expenses by category
                </div>
              </TableCell>
            </TableRow>
          )}
          {isExpanded && expCats.map(([cat, v]) => (
            <TableRow key={`exp-${row.entityId}-${cat}`} className="bg-rose-50/10 dark:bg-rose-950/5 text-muted-foreground">
              <TableCell colSpan={33} className="py-0.5 sticky left-0 bg-background z-10 border-r">
                <div className="flex items-center justify-between gap-4 text-[10px]"
                  style={{ paddingLeft: `${(depth + 3) * 14}px` }}>
                  <span className="truncate">{cat.replace(/_/g, ' ')}</span>
                  <Badge variant="outline" className="text-[8px] shrink-0">{v.bucket}</Badge>
                  <span className="font-mono shrink-0">plan {fmtMoney(v.plan)}</span>
                  <span className="font-mono shrink-0">actual {fmtMoney(v.actual)}</span>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </Fragment>
      );
    }

    // subtotal / grand-total
    const t = sumEntityTotals(totals, row.entityIds);
    const isGrand = row.kind === 'grand-total';
    return (
      <TableRow
        key={`s-${idx}`}
        className={isGrand
          ? 'bg-slate-700 text-white font-bold border-t-2 border-slate-900 hover:bg-slate-700'
          : 'bg-muted/60 font-semibold hover:bg-muted/70'}
      >
        <TableCell className="py-1.5 text-xs sticky left-0 z-10 border-r"
          style={{ backgroundColor: isGrand ? 'rgb(51 65 85)' : undefined }}>
          {row.label}
        </TableCell>
        <MoneyBlockCells block={t.openingBalance} tone="" strong />
        <TableCell className="text-right font-mono text-[10px] border-r">{fmtMoney(t.minCapital)}</TableCell>
        <MoneyBlockCells block={t.budgetedPlanRev} tone="bg-emerald-50/40 dark:bg-emerald-950/15" strong />
        <MoneyBlockCells block={t.budgetedActualRev} tone="bg-emerald-50/60 dark:bg-emerald-950/25" strong />
        <MoneyBlockCells block={t.budgetedPlanExp} tone="bg-rose-50/40 dark:bg-rose-950/15" strong />
        <MoneyBlockCells block={t.budgetedActualExp} tone="bg-rose-50/60 dark:bg-rose-950/25" strong />
        <MoneyBlockCells block={t.unplannedPlanRev} tone="bg-emerald-100/40 dark:bg-emerald-900/20" strong />
        <MoneyBlockCells block={t.unplannedActualRev} tone="bg-emerald-100/60 dark:bg-emerald-900/30" strong />
        <MoneyBlockCells block={t.unplannedPlanExp} tone="bg-rose-100/40 dark:bg-rose-900/20" strong />
        <MoneyBlockCells block={t.unplannedActualExp} tone="bg-rose-100/60 dark:bg-rose-900/30" strong />
        <MoneyBlockCells block={t.endingBalance} tone="bg-muted/30" strong />
      </TableRow>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b bg-background shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-muted-foreground" />
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-8 w-[90px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableYears.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 border-l pl-3">
            <Label className="text-xs text-muted-foreground">Weekly breakdown</Label>
            <Switch disabled />
            <Badge variant="outline" className="text-[9px]">coming soon</Badge>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          Amounts in EUR-equivalent · click an entity row to drill into categories
        </div>
      </div>

      {/* Statement table — horizontal scroll for the 33-column layout */}
      <div className="flex-1 overflow-auto">
        <Card className="shadow-xs m-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {/* Row 1 — super-column group headers */}
                <TableRow className="bg-muted/40">
                  <TableHead rowSpan={2} className="min-w-[220px] sticky left-0 bg-muted/40 z-20 border-r align-bottom">Entity</TableHead>
                  <TableHead colSpan={3} className="text-center border-b-0 border-r">Opening Balance</TableHead>
                  <TableHead rowSpan={2} className="text-right min-w-[80px] align-bottom border-r">Reg Min</TableHead>
                  <TableHead colSpan={3} className="text-center border-b-0 bg-emerald-50/40 dark:bg-emerald-950/15 border-r">Plan Rev <span className="text-[9px] font-normal">(Budget)</span></TableHead>
                  <TableHead colSpan={3} className="text-center border-b-0 bg-emerald-50/60 dark:bg-emerald-950/25 border-r">Actual Rev <span className="text-[9px] font-normal">(Budget)</span></TableHead>
                  <TableHead colSpan={3} className="text-center border-b-0 bg-rose-50/40 dark:bg-rose-950/15 border-r">Plan Exp <span className="text-[9px] font-normal">(Budget)</span></TableHead>
                  <TableHead colSpan={3} className="text-center border-b-0 bg-rose-50/60 dark:bg-rose-950/25 border-r">Actual Exp <span className="text-[9px] font-normal">(Budget)</span></TableHead>
                  <TableHead colSpan={3} className="text-center border-b-0 bg-emerald-100/40 dark:bg-emerald-900/20 border-r">Plan Rev <span className="text-[9px] font-normal">(Unplanned)</span></TableHead>
                  <TableHead colSpan={3} className="text-center border-b-0 bg-emerald-100/60 dark:bg-emerald-900/30 border-r">Actual Rev <span className="text-[9px] font-normal">(Unplanned)</span></TableHead>
                  <TableHead colSpan={3} className="text-center border-b-0 bg-rose-100/40 dark:bg-rose-900/20 border-r">Plan Exp <span className="text-[9px] font-normal">(Unplanned)</span></TableHead>
                  <TableHead colSpan={3} className="text-center border-b-0 bg-rose-100/60 dark:bg-rose-900/30 border-r">Actual Exp <span className="text-[9px] font-normal">(Unplanned)</span></TableHead>
                  <TableHead colSpan={3} className="text-center border-b-0 bg-muted/30 border-r">Ending Balance</TableHead>
                </TableRow>
                {/* Row 2 — Fiat / Crypto / Total sub-headers, repeated per block */}
                <TableRow className="bg-muted/30 text-[9px]">
                  {/* Opening */}
                  <TableHead className="text-right">Fiat</TableHead>
                  <TableHead className="text-right">Crypto</TableHead>
                  <TableHead className="text-right border-r">Total</TableHead>
                  {/* Budget Plan Rev */}
                  <TableHead className="text-right bg-emerald-50/40 dark:bg-emerald-950/15">Fiat</TableHead>
                  <TableHead className="text-right bg-emerald-50/40 dark:bg-emerald-950/15">Crypto</TableHead>
                  <TableHead className="text-right bg-emerald-50/40 dark:bg-emerald-950/15 border-r">Total</TableHead>
                  {/* Budget Actual Rev */}
                  <TableHead className="text-right bg-emerald-50/60 dark:bg-emerald-950/25">Fiat</TableHead>
                  <TableHead className="text-right bg-emerald-50/60 dark:bg-emerald-950/25">Crypto</TableHead>
                  <TableHead className="text-right bg-emerald-50/60 dark:bg-emerald-950/25 border-r">Total</TableHead>
                  {/* Budget Plan Exp */}
                  <TableHead className="text-right bg-rose-50/40 dark:bg-rose-950/15">Fiat</TableHead>
                  <TableHead className="text-right bg-rose-50/40 dark:bg-rose-950/15">Crypto</TableHead>
                  <TableHead className="text-right bg-rose-50/40 dark:bg-rose-950/15 border-r">Total</TableHead>
                  {/* Budget Actual Exp */}
                  <TableHead className="text-right bg-rose-50/60 dark:bg-rose-950/25">Fiat</TableHead>
                  <TableHead className="text-right bg-rose-50/60 dark:bg-rose-950/25">Crypto</TableHead>
                  <TableHead className="text-right bg-rose-50/60 dark:bg-rose-950/25 border-r">Total</TableHead>
                  {/* Unplanned Plan Rev */}
                  <TableHead className="text-right bg-emerald-100/40 dark:bg-emerald-900/20">Fiat</TableHead>
                  <TableHead className="text-right bg-emerald-100/40 dark:bg-emerald-900/20">Crypto</TableHead>
                  <TableHead className="text-right bg-emerald-100/40 dark:bg-emerald-900/20 border-r">Total</TableHead>
                  {/* Unplanned Actual Rev */}
                  <TableHead className="text-right bg-emerald-100/60 dark:bg-emerald-900/30">Fiat</TableHead>
                  <TableHead className="text-right bg-emerald-100/60 dark:bg-emerald-900/30">Crypto</TableHead>
                  <TableHead className="text-right bg-emerald-100/60 dark:bg-emerald-900/30 border-r">Total</TableHead>
                  {/* Unplanned Plan Exp */}
                  <TableHead className="text-right bg-rose-100/40 dark:bg-rose-900/20">Fiat</TableHead>
                  <TableHead className="text-right bg-rose-100/40 dark:bg-rose-900/20">Crypto</TableHead>
                  <TableHead className="text-right bg-rose-100/40 dark:bg-rose-900/20 border-r">Total</TableHead>
                  {/* Unplanned Actual Exp */}
                  <TableHead className="text-right bg-rose-100/60 dark:bg-rose-900/30">Fiat</TableHead>
                  <TableHead className="text-right bg-rose-100/60 dark:bg-rose-900/30">Crypto</TableHead>
                  <TableHead className="text-right bg-rose-100/60 dark:bg-rose-900/30 border-r">Total</TableHead>
                  {/* Ending */}
                  <TableHead className="text-right bg-muted/30">Fiat</TableHead>
                  <TableHead className="text-right bg-muted/30">Crypto</TableHead>
                  <TableHead className="text-right bg-muted/30 border-r">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {STATEMENT_LAYOUT.map((row, idx) => renderRow(row, idx))}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Empty-state hint */}
        {analytics && analytics.cashflow.filter(r =>
          (r.month === month && r.year === year) ||
          (r.actual_month === month && r.actual_year === year)
        ).length === 0 && (
          <div className="mx-4 mb-4 rounded border bg-muted/20 px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
            <AlertCircle size={12} /> No cash flow items recorded for {month} {year}. Planned items live in <code className="font-mono bg-background px-1">fin.cashflow_items</code>; import from the Cashflow Excel is pending.
          </div>
        )}
      </div>
    </div>
  );
}
